import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { SAVE_FLOTA_PEMISSIONS } from "@/erp/sytem-sttings/flotas/shared/constants";
import {
  ROUTER_PATHS,
  TipoEmpleadoEnumChoice,
  gridSizeMdLg12,
  gridSizeMdLg6,
  handleErrorForm,
  newFlotaSchema,
  useCheckPermissionsArray,
  useCustomStepper,
  useLoaders,
} from "@/shared";
import {
  CreateFlotaParams,
  useCreateFlota,
  useFetchAllCountries,
  useFetchAreas,
  useFetchBodegas,
  useFetchCoberturasCiudadEnabled,
  useFetchCoberturasProvincia,
  useFetchDepartamentos,
  useUpdateFlota,
  usefetchEmpresas,
} from "@/shared/actions/erp";
import { useFetchEmpleados } from "@/shared/actions/erp/empleados";
import {
  CustomAutocomplete,
  CustomTextField,
  SampleCheckbox,
  StepperBoxScene,
  TabTextLabel,
} from "@/shared/components/ui";
import CustomCellphoneTextField from "@/shared/components/ui/CustomCellphoneTextField";
import CustomNumberTextField from "@/shared/components/ui/CustomNumberTextField";
import {
  AlertSeverityEnum,
  Area,
  City,
  Country,
  Departamento,
  Empresa,
  Province,
} from "@/shared/interfaces";
import { Empleado } from "@/shared/interfaces/erp/empleados";
import { BODEGAS } from "@/shared/interfaces/erp/inventario";
import { Flota } from "@/shared/interfaces/erp/mantenimiento-operaciones";
import { useUiStore } from "@/store/ui";
import { useAsignarZonaFlota } from "../../store";
import CustomMapFlota from "../CustomMapFlota/CustomMapFlota";
import TimeClockComponenteFlota from "../TimeClockComponenteFlota/TimeClockComponenteFlota";

const steps = ["Cobertura y Área", "Datos Generales", "Vehículo"];

const diaPicoPlaca = [
  { codigo: 1, nombre: "LUNES" },
  { codigo: 2, nombre: "MARTES" },
  { codigo: 3, nombre: "MIERCOLES" },
  { codigo: 4, nombre: "JUEVES" },
  { codigo: 5, nombre: "VIERNES" },
];

const diaCuadre = [
  { codigo: 1, nombre: "LUNES" },
  { codigo: 2, nombre: "MARTES" },
  { codigo: 3, nombre: "MIERCOLES" },
  { codigo: 4, nombre: "JUEVES" },
  { codigo: 5, nombre: "VIERNES" },
  { codigo: 6, nombre: "SABADO" },
  { codigo: 7, nombre: "DOMINGO" },
];

export type SaveFlotaProps = {
  titlePage: string;
  editFlota?: Flota;
};

type SaveFormData = CreateFlotaParams & {};

const SaveFlota: React.FC<SaveFlotaProps> = ({ titlePage, editFlota }) => {
  useCheckPermissionsArray([...SAVE_FLOTA_PEMISSIONS]);

  const returnUrlFlotasPagFlotasPage =
    ROUTER_PATHS.mantenimientoOperaciones.flotaNav;
  const navigate = useNavigate();

  ///* stepper ---------------------
  const { activeStep, disableNextStepBtn, handleBack, handleNext } =
    useCustomStepper({
      steps,
    });

  ///* local state ---------------------
  const [shrink, setShrink] = useState(false);

  ///* global state ---------------------
  const setAlert = useUiStore((s) => s.setAlert);

  ///* form ---------------------
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newFlotaSchema) as any,
    defaultValues: {
      aplicar_pico_placa: false,
      aplicar_cuadre: false,
      estado: true,
      es_bodega: true,
      bodega: null,
      ats_fisico_permitido: 5,
    },
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = form;

  const watchedCountry = form.watch("pais");
  const watchedProvince = form.watch("provincia");
  const watchedCity = form.watch("ciudad");
  const watchedEmpresa = form.watch("empresa");
  const watchedArea = form.watch("area");
  const watchedIsBodega = form.watch("es_bodega");
  const watchedDiaPicoPlaca = form.watch("aplicar_pico_placa");
  const watchedAplicaCuadre = form.watch("aplicar_cuadre");

  ///* fetch data ---------------------
  const {
    data: paisesPagingRes,
    isLoading: isLoadingCountries,
    isRefetching: isRefetchingCountries,
  } = useFetchAllCountries({
    page_size: 100,
  });
  const {
    data: provinciasPagingRes,
    isLoading: isLoadingProvicias,
    isRefetching: isRefetchingProvincias,
  } = useFetchCoberturasProvincia(
    {
      page_size: 1000,
      pais: watchedCountry,
    },
    !!watchedCountry,
  );
  const {
    data: citiesPagingRes,
    isLoading: isLoadingCities,
    isRefetching: isRefetchingCities,
  } = useFetchCoberturasCiudadEnabled({
    enabled: !!watchedProvince && !!watchedCountry,
    params: {
      page_size: 600,
      provincia: watchedProvince,
    },
  });

  /// user lo setea el back en base a esto ==============
  const {
    data: empresasPaging,
    isLoading: isLoadingEmpresas,
    isRefetching: isRefetchingEmpresas,
  } = usefetchEmpresas({
    page_size: 600,
  });
  const {
    data: areasPaging,
    isLoading: isLoadingAreas,
    isRefetching: isRefetchingAreas,
  } = useFetchAreas(
    {
      page_size: 600,
      empresa: watchedEmpresa,
    },
    !!watchedEmpresa,
  );
  const {
    data: departamentosPaging,
    isLoading: isLoadingDepartamentos,
    isRefetching: isRefetchingDepartamentos,
  } = useFetchDepartamentos(
    {
      page_size: 600,
      area: watchedArea,
    },
    !!watchedArea,
  );

  const {
    data: bodegasPaging,
    isLoading: isLoadingBodegas,
    isRefetching: isRefetchingBodegas,
  } = useFetchBodegas(
    {
      page_size: 600,
      es_externa: true,
      ciudad: watchedCity,
    },
    !!watchedCity,
  );

  const { data: dataEmpleadoPading, isLoading: isLoadingEmpleado } =
    useFetchEmpleados({
      params: {
        tipo_empleado: TipoEmpleadoEnumChoice.TECNICO,
        page_size: 1000,
      },
      enabled: true,
    });

  ///* mutations ---------------------
  const createFlota = useCreateFlota({
    navigate,
    returnUrl: returnUrlFlotasPagFlotasPage,
  });
  const updateFlota = useUpdateFlota({
    navigate,
    returnUrl: returnUrlFlotasPagFlotasPage,
  });

  ///* store

  const {
    /// todas las zonas
    zones: zonesPagingRes,
    isLoadingZone: isLoadingZonas,

    /// gaurdar lista
    listaZonas,
    setListaZonas,
  } = useAsignarZonaFlota();

  ///* handlers ---------------------
  const onSave = async () => {
    if (!isValid) return;

    if (listaZonas.length === 0)
      return setAlert(
        true,
        "Por favor, asigna una o varias zonas a la flota.",
        AlertSeverityEnum.error,
      );
    const formulario = {
      ...form.getValues(),
      dia_pico_placa:
        form.getValues().dia_pico_placa != null
          ? form.getValues().dia_pico_placa
          : 0,
      dia_cuadre:
        form.getValues().dia_cuadre != null ? form.getValues().dia_cuadre : 0,
      zona: listaZonas.at(0)?.codigo,
      zonas: listaZonas?.map((zona) => zona.codigo),
    };

    ///* upd
    if (editFlota?.id_flota) {
      return await updateFlota.mutateAsync({
        id: editFlota.id_flota,
        data: formulario,
      });
    }

    ///* create
    return await createFlota.mutateAsync(formulario as any);
  };

  ///* effects ---------------------
  useEffect(() => {
    if (editFlota?.id_flota) {
      setShrink(true);

      form.reset({
        ...editFlota,
      } as any);
    }
  }, [editFlota?.id_flota]);

  /// // alerts ------
  // province
  useEffect(() => {
    if (isLoadingProvicias || isRefetchingProvincias) return;

    if (watchedCountry && !provinciasPagingRes?.data?.length) {
      setAlert(
        true,
        "No se encontraron provincias disponibles",
        AlertSeverityEnum.warning,
      );
    }
  }, [
    watchedCountry,
    provinciasPagingRes,
    isLoadingProvicias,
    isRefetchingProvincias,
  ]);
  // city
  useEffect(() => {
    if (isLoadingCities || isRefetchingCities) return;

    if (watchedProvince && !citiesPagingRes?.data?.data?.length) {
      setAlert(
        true,
        "No se encontraron ciudades disponibles",
        AlertSeverityEnum.warning,
      );
    }
  }, [watchedProvince, citiesPagingRes, isLoadingCities, isRefetchingCities]);

  useEffect(() => {
    if (isLoadingZonas) return;

    if (!zonesPagingRes?.length) {
      setAlert(
        true,
        "No se encontraron zonas disponibles",
        AlertSeverityEnum.warning,
      );
    }

    if (editFlota?.id_flota) {
      const zonas_select = zonesPagingRes?.filter((zona) =>
        form.getValues().zonas?.includes(zona.codigo),
      );
      setListaZonas(zonas_select ?? []);
    }
  }, [zonesPagingRes, isLoadingZonas]);

  // area
  useEffect(() => {
    if (isLoadingAreas || isRefetchingAreas) return;

    if (watchedEmpresa && !areasPaging?.data?.data?.length) {
      setAlert(
        true,
        "No se encontraron áreas disponibles para la empresa seleccionada",
        AlertSeverityEnum.warning,
      );
    }
  }, [watchedEmpresa, areasPaging, isLoadingAreas, isRefetchingAreas]);
  // departamento
  useEffect(() => {
    if (isLoadingDepartamentos || isRefetchingDepartamentos) return;

    if (watchedArea && !departamentosPaging?.data?.data?.length) {
      setAlert(
        true,
        "No se encontraron departamentos disponibles para el área seleccionada",
        AlertSeverityEnum.warning,
      );
    }
  }, [
    watchedArea,
    departamentosPaging,
    isLoadingDepartamentos,
    isRefetchingDepartamentos,
  ]);
  // bodega
  useEffect(() => {
    if (isLoadingBodegas || isRefetchingBodegas || !watchedCity) return;

    if (watchedCity && !bodegasPaging?.data?.data?.length) {
      setAlert(
        true,
        "No se encontraron bodegas disponibles en la ciudad seleccionada",
        AlertSeverityEnum.warning,
      );
    }
  }, [watchedCity, bodegasPaging, isLoadingBodegas, isRefetchingBodegas]);
  useLoaders(
    isLoadingCountries ||
      isLoadingCities ||
      isLoadingProvicias ||
      isRefetchingCountries ||
      isRefetchingProvincias ||
      isRefetchingCities ||
      isLoadingEmpresas ||
      isLoadingAreas ||
      isLoadingDepartamentos ||
      isRefetchingEmpresas ||
      isLoadingBodegas ||
      isRefetchingAreas ||
      isRefetchingDepartamentos ||
      isRefetchingBodegas,
  );

  if (
    editFlota &&
    (isLoadingCountries ||
      isLoadingProvicias ||
      isLoadingCities ||
      isLoadingEmpresas ||
      isLoadingAreas ||
      isLoadingDepartamentos ||
      isLoadingBodegas)
  )
    return null;

  return (
    <>
      <StepperBoxScene
        titlePage={titlePage}
        py={1}
        // steps
        steps={steps}
        activeStep={activeStep}
        handleNext={handleNext}
        handleBack={handleBack}
        disableNextStepBtn={disableNextStepBtn}
        // action btns
        onCancel={() => navigate(returnUrlFlotasPagFlotasPage)}
        onSave={handleSubmit(onSave, (error) => {
          handleErrorForm(error as any);
        })}
      >
        {/* ============= Cobertura & Area Info ============= */}
        {activeStep === 0 && (
          <Grid item container spacing={3} justifyContent="space-between">
            <Typography
              variant="h6"
              sx={{
                display: "flex",
                alignItems: "start",
                justifyContent: "start",
                width: "100%",
                pl: "1rem",
                mt: "1rem",
                color: "#6c737fb0",
                fontSize: "1rem",
              }}
            >
              ORGANIZACIÓN
            </Typography>

            <CustomAutocomplete<Empresa>
              label="Empresa"
              name="empresa"
              // options
              options={empresasPaging?.data.data || []}
              valueKey="razon_social"
              actualValueKey="codigo"
              defaultValue={form.getValues().empresa || ""}
              optionLabelForEdit={form.getValues().empresa || ""}
              isLoadingData={isLoadingEmpresas}
              // validation
              control={control}
              error={errors.empresa}
              helperText={errors.empresa?.message}
              onChangeValue={() => {
                form.setValue("area", "");
                form.setValue("departamento", "");
              }}
            />
            <CustomAutocomplete<Area>
              label="Área"
              name="area"
              // options
              options={areasPaging?.data.data || []}
              valueKey="nombre"
              actualValueKey="codigo"
              defaultValue={form.getValues().area || ""}
              optionLabelForEdit={form.getValues().area || ""}
              isLoadingData={isLoadingAreas}
              // validation
              control={control}
              error={errors.area}
              helperText={errors.area?.message}
              size={gridSizeMdLg6}
              onChangeValue={() => {
                form.setValue("departamento", "");
              }}
            />
            <CustomAutocomplete<Departamento>
              label="Departamento"
              name="departamento"
              // options
              options={departamentosPaging?.data.data || []}
              valueKey="nombre"
              actualValueKey="codigo"
              defaultValue={form.getValues().departamento || ""}
              optionLabelForEdit={form.getValues().departamento || ""}
              isLoadingData={isLoadingDepartamentos}
              // validation
              control={control}
              error={errors.departamento}
              helperText={errors.departamento?.message}
              size={gridSizeMdLg6}
            />

            <Typography
              variant="h6"
              sx={{
                display: "flex",
                alignItems: "start",
                justifyContent: "start",
                width: "100%",
                pl: "24px",
                mt: "27px",
                color: "#6c737fb0",
                fontSize: ".9rem",
              }}
            >
              COBERTURA
            </Typography>

            {/* =========== Autocomplete =========== */}
            <CustomAutocomplete<Country>
              label="País"
              name="pais"
              // options
              options={paisesPagingRes?.data?.data || []}
              valueKey="nombre"
              actualValueKey="codigo"
              defaultValue={form.getValues().pais}
              optionLabelForEdit={form.getValues().pais || ""}
              isLoadingData={isLoadingCountries}
              // validation
              control={control}
              error={errors.pais}
              helperText={errors.pais?.message}
              size={gridSizeMdLg12}
              onChangeValue={() => {
                form.setValue("provincia", "");
                form.setValue("ciudad", "");
                //form.setValue('zonas_select', []);
                setListaZonas([]);
              }}
            />
            {form.getValues().pais && (
              <CustomAutocomplete<Province>
                label="Provincia"
                name="provincia"
                // options
                options={provinciasPagingRes?.data || []}
                valueKey="nombre"
                actualValueKey="codigo"
                defaultValue={form.getValues().provincia || ""}
                optionLabelForEdit={form.getValues().provincia || ""}
                isLoadingData={isLoadingProvicias}
                // validation
                control={control}
                error={errors.provincia}
                helperText={errors.provincia?.message}
                size={gridSizeMdLg6}
                onChangeValue={() => {
                  form.setValue("ciudad", "");
                  setListaZonas([]);
                }}
              />
            )}

            {form.getValues().provincia && (
              <CustomAutocomplete<City>
                label="Ciudad"
                name="ciudad"
                // options
                options={citiesPagingRes?.data?.data || []}
                valueKey="nombre"
                actualValueKey="codigo"
                defaultValue={form.getValues().ciudad || ""}
                optionLabelForEdit={form.getValues().ciudad}
                isLoadingData={isLoadingCities}
                // validation
                control={control}
                error={errors.ciudad}
                helperText={errors.ciudad?.message}
                size={gridSizeMdLg6}
                onChangeValue={() => {
                  setListaZonas([]);
                }}
              />
            )}

            {form.getValues().ciudad && (
              <Grid item xs={12}>
                <CustomMapFlota esModal={false} />
              </Grid>
            )}
            {/* =========== USER based on Department & Cargo =========== */}
          </Grid>
        )}

        {/* ============= General Info ============= */}
        {activeStep === 1 && (
          <Grid item container spacing={3} justifyContent="center">
            <CustomTextField
              label="Nombre de la flota"
              name="nombre"
              control={form.control}
              defaultValue={form.getValues().nombre || ""}
              error={errors.nombre}
              helperText={errors.nombre?.message}
              shrink={shrink}
            />

            <CustomAutocomplete<Empleado>
              label="Lider"
              name="lider"
              // options
              options={dataEmpleadoPading?.data.data || []}
              valueKey="razon_social"
              actualValueKey="codigo"
              defaultValue={form.getValues().lider || ""}
              optionLabelForEdit={form.getValues().lider || ""}
              isLoadingData={isLoadingEmpleado}
              // validation
              control={control}
              error={errors.lider}
              helperText={errors.lider?.message}
            />

            <CustomTextField
              label="Auxiliar"
              name="auxiliar"
              control={form.control}
              defaultValue={form.getValues().auxiliar || ""}
              error={errors.auxiliar}
              helperText={errors.auxiliar?.message}
              shrink={shrink}
              size={gridSizeMdLg6}
            />

            <CustomCellphoneTextField
              name="telefono_1"
              label="Teléfono 1"
              control={control}
              defaultValue={form.getValues().telefono_1 || ""}
              error={errors.telefono_1}
              helperText={errors.telefono_1?.message}
              shrink={shrink}
              size={gridSizeMdLg6}
            />
            <CustomCellphoneTextField
              name="telefono_2"
              label="Teléfono 2"
              control={form.control}
              defaultValue={form.getValues().telefono_2 || ""}
              error={errors.telefono_2}
              helperText={errors.telefono_2?.message}
              shrink={shrink}
              size={gridSizeMdLg6}
            />
            <CustomCellphoneTextField
              label="Teléfono 3"
              name="telefono_3"
              control={form.control}
              defaultValue={form.getValues().telefono_3 || ""}
              error={errors.telefono_3}
              helperText={errors.telefono_3?.message}
              shrink={shrink}
              size={gridSizeMdLg6}
              required={false}
            />
            <CustomNumberTextField
              label="ATS FÍSICOS PERMITIDOS"
              name="ats_fisico_permitido"
              control={form.control}
              defaultValue={form.getValues().ats_fisico_permitido || "5"}
              error={errors.ats_fisico_permitido}
              helperText={errors.ats_fisico_permitido?.message}
              shrink={shrink}
              min={0}
              max={5}
              size={gridSizeMdLg6}
              disabled
            />
            <Grid {...gridSizeMdLg6} />
            <CustomTextField
              label="Correo electrónico"
              name="email"
              control={form.control}
              defaultValue={form.getValues().email || ""}
              error={errors.email}
              helperText={errors.email?.message}
              shrink={shrink}
              type="email"
            />
          </Grid>
        )}

        {/* ============= Vehicle Info ============= */}
        {activeStep === 2 && (
          <Grid item container spacing={3} justifyContent="start">
            {/* ====== vehiculo ====== */}
            <CustomTextField
              label="Marca"
              name="marca_vehiculo"
              control={form.control}
              defaultValue={form.getValues().marca_vehiculo || ""}
              error={errors.marca_vehiculo}
              helperText={errors.marca_vehiculo?.message}
              shrink={shrink}
              size={gridSizeMdLg6}
            />
            <CustomTextField
              label="Modelo"
              name="modelo_vehiculo"
              control={form.control}
              defaultValue={form.getValues().modelo_vehiculo || ""}
              error={errors.modelo_vehiculo}
              helperText={errors.modelo_vehiculo?.message}
              shrink={shrink}
              size={gridSizeMdLg6}
            />
            <CustomTextField
              label="Placa"
              name="placa_vehiculo"
              control={form.control}
              defaultValue={form.getValues().placa_vehiculo || ""}
              error={errors.placa_vehiculo}
              helperText={errors.placa_vehiculo?.message}
              shrink={shrink}
              size={gridSizeMdLg6}
            />
            <CustomNumberTextField
              label="Año"
              name="anio_vehiculo"
              control={form.control}
              defaultValue={form.getValues().anio_vehiculo || ""}
              error={errors.anio_vehiculo}
              helperText={errors.anio_vehiculo?.message}
              shrink={shrink}
              min={1970}
              max={new Date().getFullYear() + 24}
              size={gridSizeMdLg6}
            />
            <CustomTextField
              label="Color"
              name="color_vehiculo"
              control={form.control}
              defaultValue={form.getValues().color_vehiculo || ""}
              error={errors.color_vehiculo}
              helperText={errors.color_vehiculo?.message}
              shrink={shrink}
              size={gridSizeMdLg6}
            />

            <SampleCheckbox
              label="Estado"
              name="estado"
              control={form.control}
              defaultValue={!!form.getValues().estado}
              size={gridSizeMdLg6}
              isState
            />

            {/* ====== state ====== */}

            {/* <SampleCheckbox
              label="Flota Hogar"
              name="es_hogar"
              control={form.control}
              defaultValue={true}
              // defaultValue={!!form.getValues().es_hogar}
              size={gridSizeMdLg6}
            /> */}

            <SampleCheckbox
              label="Flota PYME"
              name="es_pyme"
              control={form.control}
              defaultValue={!!form.getValues().es_pyme}
              size={gridSizeMdLg6}
            />

            <SampleCheckbox
              label="Flota Corporativo"
              name="es_corporativo"
              control={form.control}
              defaultValue={!!form.getValues().es_corporativo}
              size={gridSizeMdLg6}
            />


            <SampleCheckbox
              label="Flota Corporativo"
              name="es_corporativo"
              control={form.control}
              defaultValue={!!form.getValues().es_corporativo}
              size={gridSizeMdLg6}
            />

            <SampleCheckbox
              label="Es bodega"
              name="es_bodega"
              control={form.control}
              defaultValue={form.getValues().es_bodega || false}
              size={gridSizeMdLg6}
              onChangeValue={() => {
                form.setValue("bodega", "");
              }}
            />

            {!!watchedIsBodega ? (
              <CustomAutocomplete<BODEGAS>
                label="Bodega Externa"
                name="bodega"
                options={
                  bodegasPaging?.data.data.filter((e) => e.es_externa) || []
                }
                valueKey="nombre"
                actualValueKey="codigo"
                defaultValue={form.getValues().bodega || ""}
                optionLabelForEdit={form.getValues().bodega || ""}
                isLoadingData={isLoadingBodegas}
                control={control}
                error={errors.bodega}
                helperText={errors.bodega?.message}
              />
            ) : null}

            <SampleCheckbox
              label="Aplicar Pico y Placa"
              name="aplicar_pico_placa"
              control={form.control}
              defaultValue={!!form.getValues().aplicar_pico_placa}
              size={gridSizeMdLg6}
              onChangeValue={(e) => {
                if (!e) {
                  form.setValue("dia_pico_placa", null);
                  form.setValue("hora_fin_pico_placa_1", null);
                  form.setValue("hora_fin_pico_placa_2", null);
                  form.setValue("hora_inicio_pico_placa_1", null);
                  form.setValue("hora_inicio_pico_placa_2", null);
                }
              }}
            />

            {watchedDiaPicoPlaca && (
              <>
                <CustomAutocomplete<any>
                  label="Dia pico y placa"
                  name="dia_pico_placa"
                  // options
                  options={diaPicoPlaca || []}
                  valueKey="nombre" // name is the fk
                  actualValueKey="codigo"
                  defaultValue={form.getValues().dia_pico_placa || ""}
                  isLoadingData={false}
                  control={control}
                  error={errors.dia_pico_placa}
                  helperText={errors.dia_pico_placa?.message}
                />

                <Grid item xs={12}>
                  <TabTextLabel
                    text={"HORARIO EN LA MAÑANA"}
                    mt={0}
                    pb={0}
                    pl={0}
                    pt={2}
                  />
                </Grid>

                <TimeClockComponenteFlota
                  size={gridSizeMdLg6}
                  control={control}
                  label="Hora de inicio pico y placa AM"
                  name="hora_inicio_pico_placa_1"
                  error={errors.hora_inicio_pico_placa_1}
                  helperText={errors.hora_inicio_pico_placa_1?.message}
                  defaultValue={form.getValues().hora_inicio_pico_placa_1}
                  showTimeViewClock={true}
                  isAm={true}
                />
                <TimeClockComponenteFlota
                  size={gridSizeMdLg6}
                  control={control}
                  label="Hora de fin pico y placa AM"
                  name="hora_fin_pico_placa_1"
                  error={errors.hora_fin_pico_placa_1}
                  helperText={errors.hora_fin_pico_placa_1?.message}
                  defaultValue={form.getValues().hora_fin_pico_placa_1}
                  showTimeViewClock={true}
                  isAm={true}
                />

                <Grid item xs={12}>
                  <TabTextLabel
                    text={"HORARIO EN LA TARDE"}
                    mt={0}
                    pb={0}
                    pl={0}
                    pt={2}
                  />
                </Grid>

                <TimeClockComponenteFlota
                  size={gridSizeMdLg6}
                  control={control}
                  label="Hora de inicio pico y placa PM"
                  name="hora_inicio_pico_placa_2"
                  error={errors.hora_inicio_pico_placa_2}
                  helperText={errors.hora_inicio_pico_placa_2?.message}
                  defaultValue={form.getValues().hora_inicio_pico_placa_2}
                  showTimeViewClock={true}
                  isPM={true}
                />

                <TimeClockComponenteFlota
                  size={gridSizeMdLg6}
                  control={control}
                  label="Hora de fin pico y placa PM"
                  name="hora_fin_pico_placa_2"
                  error={errors.hora_fin_pico_placa_2}
                  helperText={errors.hora_fin_pico_placa_2?.message}
                  defaultValue={form.getValues().hora_fin_pico_placa_2}
                  showTimeViewClock={true}
                  isPM={true}
                />
              </>
            )}

            <SampleCheckbox
              label="Aplicar Cuadre"
              name="aplicar_cuadre"
              control={form.control}
              defaultValue={!!form.getValues().aplicar_cuadre}
              size={gridSizeMdLg6}
              onChangeValue={(e) => {
                if (!e) {
                  form.setValue("dia_cuadre", null);
                  form.setValue("hora_fin_cuadre", null);
                  form.setValue("hora_inicio_cuadre", null);
                }
              }}
            />

            {!!watchedAplicaCuadre && (
              <>
                <CustomAutocomplete<any>
                  label="Dia Cuadre"
                  name="dia_cuadre"
                  // options
                  options={diaCuadre || []}
                  valueKey="nombre" // name is the fk
                  actualValueKey="codigo"
                  defaultValue={form.getValues().dia_cuadre || ""}
                  isLoadingData={false}
                  control={control}
                  error={errors.dia_cuadre}
                  helperText={errors.dia_cuadre?.message}
                />

                <Grid item xs={12}>
                  <TabTextLabel
                    text={"HORARIO CUADRE"}
                    mt={0}
                    pb={0}
                    pl={0}
                    pt={2}
                  />
                </Grid>

                <TimeClockComponenteFlota
                  size={gridSizeMdLg6}
                  control={control}
                  label="Hora Inicio Cuadre"
                  name="hora_inicio_cuadre"
                  error={errors.hora_inicio_cuadre}
                  helperText={errors.hora_inicio_cuadre?.message}
                  defaultValue={form.getValues().hora_inicio_cuadre}
                  showTimeViewClock={true}
                />
                <TimeClockComponenteFlota
                  size={gridSizeMdLg6}
                  control={control}
                  label="Hora Fin Cuadre"
                  name="hora_fin_cuadre"
                  error={errors.hora_fin_cuadre}
                  helperText={errors.hora_fin_cuadre?.message}
                  defaultValue={form.getValues().hora_fin_cuadre}
                  showTimeViewClock={true}
                />
              </>
            )}
          </Grid>
        )}
      </StepperBoxScene>
    </>
  );
};

export default SaveFlota;
